import { Chip } from '@mui/material';
import {
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
} from '@mui/icons-material';
import type { GroupCreationRequestStatus } from '@penticton-dev-events/shared';

export interface GroupCreationRequestStatusBadgeProps {
  readonly status: GroupCreationRequestStatus;
  readonly size?: 'small' | 'medium';
}

/**
 * Reusable status badge for group creation requests
 * Provides consistent visual indicators across the application
 *
 * Accessibility:
 * - Color-blind safe: Uses icon + text + color
 * - High contrast color scheme
 * - Semantic color coding (blue=pending, green=approved, red=rejected)
 */
const GroupCreationRequestStatusBadge = ({
  status,
  size = 'medium',
}: GroupCreationRequestStatusBadgeProps) => {
  const statusConfig = {
    pending: {
      label: 'Pending',
      color: 'info' as const,
      icon: <PendingIcon fontSize="small" />,
      ariaLabel: 'Request status: pending review',
    },
    approved: {
      label: 'Approved',
      color: 'success' as const,
      icon: <ApprovedIcon fontSize="small" />,
      ariaLabel: 'Request status: approved',
    },
    rejected: {
      label: 'Rejected',
      color: 'error' as const,
      icon: <RejectedIcon fontSize="small" />,
      ariaLabel: 'Request status: rejected',
    },
  };

  const config = statusConfig[status];

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size={size}
      aria-label={config.ariaLabel}
      sx={theme => ({
        fontWeight: 600,
        ...(theme.palette.mode === 'dark' &&
          status === 'pending' && {
            // Ensure good contrast in dark mode for info chips
            backgroundColor: theme.palette.info.dark,
            color: theme.palette.info.contrastText,
          }),
      })}
    />
  );
};

export default GroupCreationRequestStatusBadge;
